/**
 * EncounterAuditRow Component
 *
 * Single row displaying encounter audit summary information
 */

import React, { useMemo } from 'react';
import { Avatar } from '@components/primitives/Avatar';
import {
  ChevronRight as ChevronIcon,
  EventNote as EncounterIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Videocam as VideoIcon,
} from '@mui/icons-material';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import type { EncounterAuditSummary } from '../../types';
import { QualityScore } from '../shared/QualityScore';

/**
 * Get patient name parts from patient object or fallback to patient_name string
 */
function getPatientNameParts(
  patient: { first_name?: string | null; last_name?: string | null } | null | undefined,
  patientName: string | null | undefined,
): {
  firstName: string;
  lastName: string;
} {
  // Prefer structured patient data
  if (patient?.first_name || patient?.last_name) {
    return {
      firstName: patient.first_name || '',
      lastName: patient.last_name || '',
    };
  }

  // Fallback to parsing patient_name string
  if (!patientName) return { firstName: '', lastName: '' };

  const parts = patientName.trim().split(/\s+/);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };

  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  };
}

/**
 * Parse provider name into first and last name parts
 */
function getProviderNameParts(providerName: string | null | undefined): {
  firstName: string;
  lastName: string;
} {
  if (!providerName) return { firstName: '', lastName: '' };

  const parts = providerName.trim().split(/\s+/);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };

  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  };
}

interface EncounterAuditRowProps {
  /** Encounter audit data */
  encounter: EncounterAuditSummary;
  /** Click handler to view encounter details */
  onViewEncounter?: (encounterId: string) => void;
}

/**
 * Format date to short display format
 */
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '—';
  }
};

/**
 * Format time to display format
 */
const formatTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

/**
 * Get modality icon based on encounter modality (for the icon column)
 */
const getModalityIcon = (modality: string | null | undefined) => {
  switch (modality) {
    case 'video':
      return <VideoIcon fontSize="small" />;
    case 'audio':
      return <PhoneIcon fontSize="small" />;
    case 'in_person':
      return <PersonIcon fontSize="small" />;
    default:
      return <EncounterIcon fontSize="small" />;
  }
};

export const EncounterAuditRow: React.FC<EncounterAuditRowProps> = ({
  encounter,
  onViewEncounter,
}) => {
  const theme = useTheme();

  const handleRowClick = () => {
    onViewEncounter?.(encounter.encounter_id);
  };

  const compliancePct = encounter.latest_compliance_pct;
  const hasAudit = encounter.has_audit;

  // Get patient name parts for Avatar
  const patientNameParts = useMemo(
    () => getPatientNameParts(encounter.patient, encounter.patient_name),
    [encounter.patient, encounter.patient_name],
  );

  // Get provider name parts for Avatar
  const providerNameParts = useMemo(
    () => getProviderNameParts(encounter.provider_name),
    [encounter.provider_name],
  );

  // Display name with fallback chain: preferred_name → full_name → patient_name
  const displayName = useMemo(() => {
    if (encounter.patient?.preferred_name) return encounter.patient.preferred_name;
    if (encounter.patient?.first_name) {
      return [encounter.patient.first_name, encounter.patient.last_name]
        .filter(Boolean)
        .join(' ');
    }
    return encounter.patient_name || 'Patient';
  }, [encounter.patient, encounter.patient_name]);

  const hasPatient = Boolean(encounter.patient_id);
  const hasProvider = Boolean(encounter.provider_name);

  // Use appointment start_time if available, otherwise scheduled_for
  const dateTimeSource = encounter.appointment?.start_time ?? encounter.scheduled_for;

  // Session type canonical name
  const sessionType = encounter.session_type?.canonical || null;

  // CPT Analysis for badge color
  const cptAnalysis = (encounter as any).cpt_analysis;
  const targetCodes = cptAnalysis?.target_cpt_codes;
  const cptSource = cptAnalysis?.target_cpt_source;
  const cptMatch = cptAnalysis?.cpt_match;

  // Determine CPT badge color and tooltip
  let cptBadgeColor: string = theme.palette.info.main; // Default blue
  let cptTooltip: string = '';

  if (cptSource === 'user') {
    if (cptMatch === true) {
      cptBadgeColor = theme.palette.success.main; // Green for approved
      cptTooltip = 'Audit based on your codes (matches documentation)';
    } else if (cptMatch === false) {
      cptBadgeColor = theme.palette.warning.main; // Yellow for mismatch
      cptTooltip = 'Audit based on your codes (AI suggests different based on documentation)';
    }
  } else if (cptSource === 'ai') {
    cptBadgeColor = theme.palette.info.main; // Blue for AI suggested
    cptTooltip = 'Audit based on AI-suggested codes from documentation';
  }

  return (
    <Box
      onClick={handleRowClick}
      sx={{
        px: 2,
        py: 1.5,
        borderBottom: `1px solid ${theme.palette.grey[100]}`,
        cursor: onViewEncounter ? 'pointer' : 'default',
        transition: 'background-color 0.15s ease',
        '&:hover': onViewEncounter
          ? {
              bgcolor: theme.palette.grey[50],
            }
          : {},
        '&:last-child': {
          borderBottom: 'none',
        },
      }}
    >
      {/* First Row - Main Info */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '40px 120px 1fr 1fr 1fr 150px 80px 100px 80px 40px',
          gap: 2,
          alignItems: 'start',
        }}
      >
      {/* Encounter Icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 40,
          height: 40,
          borderRadius: 1.5,
          bgcolor: theme.palette.primary.light,
          color: theme.palette.primary.contrastText,
        }}
      >
        {getModalityIcon(encounter.modality)}
      </Box>

      {/* Date & Time Combined */}
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 40 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {formatDate(dateTimeSource)}
        </Typography>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem' }}>
          {formatTime(dateTimeSource)}
        </Typography>
        {encounter.appointment?.duration && (
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem' }}>
            {encounter.appointment.duration} min
          </Typography>
        )}
      </Box>

      {/* Provider with Avatar */}
      <Box>
        {hasProvider ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar
              id={encounter.provider_name || 'unknown'}
              firstName={providerNameParts.firstName}
              lastName={providerNameParts.lastName}
              sx={{
                width: 32,
                height: 32,
                fontSize: '0.75rem',
                fontWeight: 600,
                flexShrink: 0,
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={encounter.provider_name || undefined}
              >
                {encounter.provider_name}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
            —
          </Typography>
        )}
      </Box>

      {/* Patient Name with Avatar */}
      <Box>
        {hasPatient && encounter.patient_id ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar
              id={encounter.patient_id}
              firstName={patientNameParts.firstName}
              lastName={patientNameParts.lastName}
              sx={{
                width: 32,
                height: 32,
                fontSize: '0.75rem',
                fontWeight: 600,
                flexShrink: 0,
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={displayName}
              >
                {displayName}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
            —
          </Typography>
        )}
      </Box>

      {/* Session Type */}
      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
        <Typography
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: theme.palette.text.secondary,
          }}
          title={sessionType || undefined}
        >
          {sessionType || '—'}
        </Typography>
      </Box>

      {/* CPT Codes */}
      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40, gap: 0.5, flexWrap: 'wrap' }}>
        {targetCodes && targetCodes.length > 0 ? (
          <Tooltip title={cptTooltip} arrow placement="top">
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {targetCodes.slice(0, 2).map((code: string, idx: number) => (
                <Chip
                  key={idx}
                  label={code}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    bgcolor: `${cptBadgeColor}15`,
                    color: cptBadgeColor,
                    border: `1px solid ${cptBadgeColor}50`,
                    '& .MuiChip-label': {
                      px: 0.75,
                    },
                  }}
                />
              ))}
            </Box>
          </Tooltip>
        ) : (
          <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
            —
          </Typography>
        )}
      </Box>

      {/* Signed Status */}
      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
        {encounter.note_id ? (
          <Chip
            label={(encounter as any).is_signed ? 'Signed' : 'Draft'}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.7rem',
              fontWeight: 600,
              bgcolor: (encounter as any).is_signed 
                ? `${theme.palette.success.main}15` 
                : `${theme.palette.grey[500]}15`,
              color: (encounter as any).is_signed 
                ? theme.palette.success.main 
                : theme.palette.grey[600],
              border: (encounter as any).is_signed
                ? `1px solid ${theme.palette.success.main}50`
                : `1px solid ${theme.palette.grey[400]}50`,
              '& .MuiChip-label': {
                px: 0.75,
              },
            }}
          />
        ) : (
          <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
            —
          </Typography>
        )}
      </Box>

      {/* Compliance % */}
      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
        {hasAudit && compliancePct !== null && compliancePct !== undefined ? (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color:
                compliancePct >= 80
                  ? theme.palette.success.main
                  : compliancePct >= 60
                    ? theme.palette.warning.main
                    : theme.palette.error.main,
            }}
          >
            {compliancePct.toFixed(0)}%
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
            —
          </Typography>
        )}
      </Box>

      {/* Quality Score */}
      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
        {hasAudit ? (
          <QualityScore
            score={encounter.latest_quality_score}
            variant="badge"
            size="small"
          />
        ) : (
          <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
            —
          </Typography>
        )}
      </Box>

      {/* Chevron */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          minHeight: 40,
        }}
      >
        {onViewEncounter && (
          <ChevronIcon fontSize="small" sx={{ color: theme.palette.grey[400] }} />
        )}
      </Box>
      </Box>

      {/* Second Row - Summary */}
      {encounter.summary && (
        <Box
          sx={{
            mt: 1,
            pl: '162px', // Align with content after icon + date column + gaps (40px + 120px + 2px)
            pr: '56px', // Account for chevron space
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.disabled,
              lineHeight: 1.4,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {encounter.summary}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
